import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import config from '../../config/config';
import useUser from '../../hooks/useUser';
import { formatCaseName }  from '../../helpers/cases';
import { useGetCasesQuery } from '../../redux/services/caseApi';
import EditCaseButton from './EditCaseButton';
import CustomInput from '../common/CustomInput';
import CustomInputLabel from '../common/CustomInputLabel';

const getOptions = objects => (
  (objects || []).map(obj => ({
    label: formatCaseName(obj),
    value: obj.id,
  }))
);

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({name: value, limit});
};

const handleSubmit = ({debouncedTextChangeHandler, onSubmit, setCaseId}) => (e,val,reason) => {
  if (reason === 'selectOption') {
    e.target = {...(e.target || {}), value: val.value};
    if (val.value) {
      setCaseId(val.value);
      return onSubmit(e, {id: val.value});
    }
    if (val.id) {
      setCaseId(val.id);
      return onSubmit(e, {id: val.id});
    }
    if (val.val === null || val.value === null) {
      setCaseId(null);
      return onSubmit(e, {id: null});
    }
  }
  debouncedTextChangeHandler(e);
};

const FindCaseInput = props => {
  const {
    onSubmit, 
    setQuery,
    cases,
    isLoading,
    limit,
    caseId,
    setCaseId,
    textInput,
    setTextInput,
  } = props;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  const options = getOptions(cases);
  const submitHandler = handleSubmit({debouncedTextChangeHandler, onSubmit, setCaseId});
  if (isLoading) return;
  if (!!caseId) {
    return (
      <Stack>
        <CustomInputLabel required={props.required}>
          {props.label || 'Case'}
        </CustomInputLabel>
        <Box>
          <Chip
            color='default'
            label={
              caseId
              ? formatCaseName((cases || []).find(c => c.id === caseId))
              : ''
            }
            onDelete={e => 
              submitHandler(
                {...e, target: {...e.target, value: null}},
                {id: null, value: null},
                'selectOption'
              )
            }
          />
        </Box>
      </Stack>
    );
  }
  return (
    <Stack gap={.25}>
      <Stack direction='row' spacing={0.25} alignItems='center'>
        <CustomInput {...props}>
          <Autocomplete
            type='text'
            style={{minWidth: props.minWidth || null, maxWidth: props.maxWidth || null}}
            options={options}
            disabled={!props.patientId || !options?.length}
            filterOptions={x => x}
            loading={isLoading}
            onChange={submitHandler}
            renderInput={params => (
              <TextField
                placeholder={(props.patientId ? props.placeholder : 'Select a Patient First') || 'Search Cases'}
                onChange={debouncedTextChangeHandler}
                {...params}
                sx={props.sx}
                size={props.size}
                fullWidth={props.fullWidth}
                className={`${props.rounded && 'custom-text-field-rounded'}`}
                minWidth={props.minWidth}
                value={textInput}
                InputProps={{
                  ...params.InputProps,
                  autoFocus: props.autoFocus,
                  startAdornment: <InputAdornment position="start">
                    <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
                  </InputAdornment>,
                  endAdornment: (
                    <React.Fragment>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            {...pick(
              props, 
              'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
              'size',
              'fullWidth',
              'minWidth'
            )}
              />
        </CustomInput>
        <Stack direction='row' spacing={0.25} alignItems='center' paddingTop={4}>
          {
            props?.allowCreate && props?.patientId && <EditCaseButton
              editing={false}
              patientId={props.patientId} 
              variant='text'
              size='small'
              buttonText='Create'
              iconOnly
              afterSubmit={(e, {id}) => setCaseId(id)}
              session={props?.session}
            />
          }
          {
            props?.allowEdit && props?.patientId && caseId && <EditCaseButton
              editing={true}
              caseId={caseId || props?.defaultValue?.id}
              patientId={props.patientId} 
              variant='text'
              size='small'
              buttonText='Edit'
              iconOnly
              afterSubmit={(e, {id}) => setCaseId(id)}
            />
          }
        </Stack>
      </Stack>
    </Stack>
  );
};

const FieldCaseInputContainer = props => {
  const {patientId, limit} = props;
  const [query, setQuery] = useState({limit});
  const [caseId, setCaseId] = useState(props.defaultValue);
  const [textInput, setTextInput] = useState('');
  const [prevPatientId, setPrevPatientId] = useState(null);
  const user = useUser();
  const casesQuery = useGetCasesQuery(
    {...query, patient_id: patientId, include_nonowned: props.includeNonOwned},
    {skip: !patientId}
  );
  useEffect(() => {
    if (!prevPatientId) return setPrevPatientId(patientId);
    if (prevPatientId && prevPatientId !== patientId) { 
      setCaseId(props.defaultValue);
      setTextInput('');
      if (patientId) setPrevPatientId(patientId);
    }
  }, [patientId, prevPatientId]);
  const isLoading = casesQuery.isLoading || !user;
  const cases = (get(casesQuery, 'data.data') || []).filter(c => c.status === 'ACTIVE');
  console.log('FieldCaseInputContainer.props', props);
  // when patient id changes, set caseId and textInput to blank
  if (!user || isLoading || !cases) return null;
  const defaultValue = (
    props.defaultValue 
    ? (cases || [])
      .filter(c => c.id === props.defaultValue)
      .map(c => ({value: c.id, label: formatCaseName(c), id: c.id}))
      ?.[0]
    : null
  );
  const childProps = {
    ...props,
    caseId,
    setCaseId,
    textInput,
    setTextInput,
    user,
    casesQuery,
    isLoading,
    setQuery,
    cases,
    defaultValue,
  };
  return <FindCaseInput {...childProps} />;
};

FieldCaseInputContainer.defaultProps = {
  autoFocus: false,
  defaultValue: null,
  disableClearable: false,
  freeSolo: true,
  id: 'FindCaseInput.Autocomplete',
  placeholder: 'Search Cases',
  onSubmit: (e, {id}) => console.log('FindCaseInput.onSubmit', id),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
  includeNonOwned: false,
};

FieldCaseInputContainer.propTypes = {
  patientId: PropTypes.number.isRequired,
  autoFocus: PropTypes.bool,
  defaultValue: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
  includeNonOwned: PropTypes.bool,
};

export default FieldCaseInputContainer;